import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Handshake, Heart, Loader2, Plane, Users } from "lucide-react";
import { CityCombobox } from "@/components/apply/CityCombobox";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, INTEREST_KEYS, TYPE_LABELS } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { LocalPhotoPicker, MenFields, netWorthLabel, TRACKS, TrackPicker, uploadApplicationPhotos } from "@/components/apply/TrackFields";

const INTENTS = [
  { id: "find_cohosts", label: "Find co-hosts", icon: Users },
  { id: "create_experiences", label: "Create experiences", icon: Plane },
  { id: "networking", label: "Networking", icon: Handshake },
  { id: "dating", label: "Dating", icon: Heart },
];
const STEPS = ["About you", "Background & profile", "Review"];

const Stepper = ({ step, steps }) => (
  <ol className="mb-10 flex items-center gap-3" aria-label="Application progress">
    {steps.map((s, i) => (
      <li key={s} className="flex items-center gap-3" aria-current={i === step ? "step" : undefined}>
        {i > 0 && <span className="h-px w-5 bg-foreground/25 sm:w-8" aria-hidden />}
        <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-sm", i < step ? "border-primary bg-primary text-primary-foreground" : i === step ? "border-primary text-primary" : "border-foreground/30 text-muted-foreground")}>
          {i < step ? <Check className="h-4 w-4" /> : `0${i + 1}`}
        </span>
        <span className={cn("hidden text-sm sm:inline", i === step ? "font-semibold text-primary" : "text-muted-foreground")}>{s}</span>
      </li>
    ))}
  </ol>
);

const FieldError = ({ msg, id }) => msg ? <p id={id} className="mt-2 text-sm text-destructive" role="alert">{msg}</p> : null;

export const CityField = CityCombobox;

export const IntentPicker = ({ value, onChange, error }) => (
  <fieldset>
    <legend className="field-label">What brings you here?</legend>
    <div className="grid gap-3 sm:grid-cols-2">
      {INTENTS.map((it) => {
        const on = value.includes(it.id);
        return (
          <button key={it.id} type="button" aria-pressed={on} data-testid={`intent-${it.id}`}
            onClick={() => onChange(on ? value.filter((v) => v !== it.id) : [...value, it.id])}
            className={cn("flex min-h-[64px] items-center gap-4 rounded-[10px] border px-5 text-left transition-colors", on ? "border-primary bg-primary/5" : "border-foreground/30 hover:border-primary/60")}>
            <it.icon className="h-6 w-6 text-primary" strokeWidth={1.5} /> <span>{it.label}</span>
          </button>
        );
      })}
    </div>
    <FieldError msg={error} />
  </fieldset>
);

export const InterestPicker = ({ value, onChange }) => (
  <fieldset>
    <legend className="field-label">Experiences you're drawn to</legend>
    <div className="flex flex-wrap gap-2">
      {INTEREST_KEYS.map((k) => [k, TYPE_LABELS[k]]).map(([k, label]) => (
        <button key={k} type="button" className="chip" data-active={value.includes(k)} aria-pressed={value.includes(k)} data-testid={`interest-${k}`}
          onClick={() => onChange(value.includes(k) ? value.filter((v) => v !== k) : [...value, k])}>{label}</button>
      ))}
    </div>
  </fieldset>
);

export default function Apply() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ track: "", name: "", email: "", password: "", home_city: "", intents: [], interests: [], bio: "", instagram: "",
    net_worth_band: "", net_worth_other: "", occupation: "", company: "", linkedin: "", reference: "", photos: [], age_confirmed: false });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!f.track) e.track = "Please choose your application track";
      if (f.instagram && !/^@?[A-Za-z0-9._]{1,30}$/.test(f.instagram.trim())) e.instagram = "Enter a valid Instagram handle";
      if (f.name.trim().length < 2) e.name = "Please enter your full name";
      if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = "Please enter a valid email address";
      if (f.password.length < 8) e.password = "Use at least 8 characters";
      if (f.home_city.trim().length < 2) e.home_city = "Please choose your home city";
      if (!f.intents.length) e.intents = "Choose at least one";
      if (!f.age_confirmed) e.age = "You must be 18 or over to apply";
    }
    if (step === 1 && f.photos.length > 6) e.photos = "Please add up to 6 photos";
    if (step === 1 && f.track === "man") {
      if (!f.net_worth_band) e.net_worth_band = "Please choose a band";
      if (f.net_worth_band === "other" && !f.net_worth_other.trim()) e.net_worth_other = "Please enter your net worth, e.g. $1m or $500k";
      if (f.occupation.trim().length < 2) e.occupation = "Please add your occupation";
      if (f.linkedin && !/^https:\/\/([a-z]{2,3}\.)?linkedin\.com\//i.test(f.linkedin.trim())) e.linkedin = "Use a https://linkedin.com/ link";
    }
    setErrors(e);
    return !Object.keys(e).length;
  };

  const next = () => validate() && setStep((s) => s + 1);

  const submit = async () => {
    setBusy(true);
    setServerError("");
    try {
      const { photos, ...body } = f;
      const { data } = await api.post("/auth/apply", { ...body, net_worth_band: body.net_worth_band || null });
      setUser(data);
      if (photos.length) {
        try {
          setUser(await uploadApplicationPhotos(photos));
        } catch (err) {
          toast.error(`Your application was submitted, but your photos didn't upload: ${apiError(err)}. You can add them on the next screen.`);
        }
      }
      navigate("/status");
    } catch (err) {
      setServerError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout testId="apply-page">
      <h1 className="font-display text-5xl leading-[0.95] sm:text-6xl">Apply for membership</h1>
      <div className="mt-8"><Stepper step={step} steps={STEPS} /></div>
      <form onSubmit={(e) => { e.preventDefault(); step < 2 ? next() : submit(); }} noValidate className="space-y-6" data-testid={`apply-step-${step}`}>
        {step === 0 && (
          <>
            <TrackPicker value={f.track} onChange={set("track")} error={errors.track} />
            <div>
              <label htmlFor="name" className="field-label">Full name</label>
              <input id="name" className="field" placeholder="Your full name" autoComplete="name" value={f.name} onChange={(e) => set("name")(e.target.value)} data-testid="apply-name-input" aria-invalid={!!errors.name} />
              <FieldError msg={errors.name} />
            </div>
            <div>
              <label htmlFor="email" className="field-label">Email address</label>
              <input id="email" type="email" className="field" placeholder="you@example.com" autoComplete="email" value={f.email} onChange={(e) => set("email")(e.target.value)} data-testid="apply-email-input" aria-invalid={!!errors.email} />
              <FieldError msg={errors.email} />
            </div>
            <div>
              <label htmlFor="password" className="field-label">Create a password</label>
              <input id="password" type="password" className="field" placeholder="At least 8 characters" autoComplete="new-password" value={f.password} onChange={(e) => set("password")(e.target.value)} data-testid="apply-password-input" aria-invalid={!!errors.password} />
              <FieldError msg={errors.password} />
            </div>
            <div>
              <label htmlFor="instagram" className="field-label">Instagram <span className="text-muted-foreground">(optional)</span></label>
              <input id="instagram" className="field" maxLength={31} placeholder="@yourhandle" value={f.instagram} onChange={(e) => set("instagram")(e.target.value)} data-testid="apply-instagram-input" aria-invalid={!!errors.instagram} />
              <FieldError msg={errors.instagram} />
            </div>
            <CityField value={f.home_city} onChange={set("home_city")} error={errors.home_city} />
            <IntentPicker value={f.intents} onChange={set("intents")} error={errors.intents} />
            <div>
              <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-sm">
                <Checkbox checked={f.age_confirmed} onCheckedChange={(v) => set("age_confirmed")(!!v)} data-testid="apply-age-checkbox" className="h-6 w-6 border-foreground/40" />
                I confirm I am 18 or over.
              </label>
              <FieldError msg={errors.age} />
            </div>
          </>
        )}
        {step === 1 && (
          <>
            {f.track === "man" && <MenFields f={f} set={set} errors={errors} />}
            <LocalPhotoPicker files={f.photos} onChange={set("photos")} error={errors.photos} />
            <InterestPicker value={f.interests} onChange={set("interests")} />
            <div>
              <label htmlFor="bio" className="field-label">A few words about you <span className="text-muted-foreground">(optional)</span></label>
              <textarea id="bio" rows={5} maxLength={600} className="field py-3" placeholder="What kind of experiences would you like to create or join?" value={f.bio} onChange={(e) => set("bio")(e.target.value)} data-testid="apply-bio-input" />
              <p className="mt-1 text-right text-xs text-muted-foreground">{f.bio.length}/600</p>
            </div>
          </>
        )}
        {step === 2 && (
          <dl className="surface divide-y divide-border" data-testid="apply-review">
            {[["Track", TRACKS.find((t) => t.id === f.track)?.label.replace("I'm applying as", "Applying as")], ["Full name", f.name], ["Email", f.email], ["Home city", f.home_city],
              ["Instagram", f.instagram || "—"],
              ...(f.track === "man" ? [["Net worth", netWorthLabel(f.net_worth_band, f.net_worth_other)], ["Occupation", f.occupation], ["Company", f.company || "—"], ["LinkedIn", f.linkedin || "—"], ["Reference", f.reference || "—"]] : []),
              ["Photos", f.photos.length ? `${f.photos.length} selected` : "None"],
              ["What brings you here", f.intents.map((i) => INTENTS.find((x) => x.id === i)?.label).join(", ")],
              ["Interests", f.interests.map((i) => TYPE_LABELS[i]).join(", ") || "—"], ["About you", f.bio || "—"]].map(([k, v]) => (
              <div key={k} className="grid gap-1 px-5 py-4 sm:grid-cols-[160px_1fr]"><dt className="text-sm text-muted-foreground">{k}</dt><dd className="break-words">{v}</dd></div>
            ))}
          </dl>
        )}
        {serverError && <p role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm" data-testid="apply-error">{serverError}</p>}
        <div className="flex gap-3">
          {step > 0 && <button type="button" className="btn-outline px-5" onClick={() => setStep((s) => s - 1)} data-testid="apply-back-button" aria-label="Back"><ArrowLeft className="h-4 w-4" /></button>}
          <button type="submit" className="btn-primary min-h-[56px] flex-1 text-lg" disabled={busy} data-testid={step < 2 ? "apply-continue-button" : "apply-submit-button"}>
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : step < 2 ? <>Continue <ArrowRight className="h-5 w-5" /></> : "Submit application"}
          </button>
        </div>
        <div className="hairline" />
        <p className="text-center text-sm text-muted-foreground">Every application is privately screened before access is granted.</p>
      </form>
    </AuthLayout>
  );
}
